import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MdAdd, MdArrowBack, MdCheck } from "react-icons/md";
import AtriumApi, { getApiErrorMessage } from "api";
import notify from "lib/notify";
import InputField from "components/fields/InputField";
import Button, { IconButton } from "components/button";
import AlertPane from "components/alert";
import Avatar from "components/avatar";
import { PageContainer, PageHeader } from "components/page";
import NewChildForm from "../register/new-child-form";
import CheckInCode from "./qrcode";

/** Page header with an optional in-flow back button for the wizard steps. */
const StepHeader = ({ description, onBack }) => (
  <div className="flex items-center gap-3">
    {onBack && (
      <IconButton
        icon={<MdArrowBack className="h-5 w-5" />}
        label="Back"
        variant="secondary"
        onClick={onBack}
      />
    )}
    <PageHeader className="min-w-0 flex-1" title="Check-in" description={description} />
  </div>
);

const CheckIn = () => {
  const [step, setStep] = useState("init");
  const [selected, setSelected] = useState([]);
  const [parent, setParent] = useState({});
  const [kids, setKids] = useState([]);

  const [queryParams] = useSearchParams();

  const [phone, setPhone] = useState(queryParams.get("phone") || "");
  const [error, setError] = useState("");
  const [errors, setErrors] = useState([]);
  const [checkinCode, setCheckinCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);

  queryParams.set("phone", "");
  const navigate = useNavigate();

  const verifyPhone = (e) => {
    e.preventDefault();
    setError("");
    if (!phone) {
      return setError("Phone number is required");
    }
    setVerifying(true);
    AtriumApi.post("/v1/check", { phone: phone })
      .then((resp) => {
        setKids(resp.data.children);
        setParent(resp.data.user);
        setStep("pick-kids");
      })
      .catch((error) => {
        if (error.response?.status === 404) {
          navigate(`/member/register?phone=${phone}`);
        } else {
          notify.error(getApiErrorMessage(error, "Could not look up that phone number"));
        }
      })
      .finally(() => setVerifying(false));
  };

  const toggleSelection = (kid) => {
    const index = selected.indexOf(kid.id);
    if (index === -1) {
      setSelected(selected.concat([kid.id]));
    } else {
      setSelected(selected.slice(0, index).concat(selected.slice(index + 1)));
    }
  };

  const setupCheckIn = () => {
    setCheckingIn(true);
    AtriumApi.post("/v1/check-in", {
      guardianId: parent.id,
      children: selected,
    })
      .then((resp) => {
        setCheckinCode(resp.data.code);
        setStep("notice");
      })
      .catch((error) => {
        notify.error(getApiErrorMessage(error, "Could not check in"));
      })
      .finally(() => setCheckingIn(false));
  };

  const onSubmit = (user) => {
    setErrors([]);
    const formData = new FormData();
    for (const key in user) {
      formData.append(key, user[key]);
    }
    formData.append("related", parent.phone);
    return AtriumApi.post("/v1/users/register", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
      .then((resp) => {
        if (resp.status === 422) {
          setErrors(resp.data.errors);
          return;
        }
        setKids([...kids.filter((x) => x.id !== resp.data.id), resp.data]);
        setStep("pick-kids");
      })
      .catch((error) => {
        notify.error(getApiErrorMessage(error, "Could not save child"));
      });
  };

  return (
    <PageContainer>
      {step === "init" && (
        <>
          <StepHeader description="Enter your phone number to check in." />
          <form onSubmit={verifyPhone} noValidate className="flex flex-col gap-4">
            <InputField
              label="Phone Number"
              placeholder="Phone number"
              type="tel"
              autoComplete="tel"
              value={phone}
              error={error}
              onChange={(e) => setPhone(e.target.value)}
            />
            <Button type="submit" size="lg" fullWidth loading={verifying}>
              Continue
            </Button>
          </form>
        </>
      )}

      {step === "pick-kids" && (
        <>
          <StepHeader
            description="Select the children to check in."
            onBack={() => setStep("init")}
          />
          <ul className="flex flex-col gap-2">
            {kids.map((kid, i) => {
              const isSelected = selected.indexOf(kid.id) !== -1;
              return (
                <li key={"kid" + i}>
                  <button
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => toggleSelection(kid)}
                    className={`focus-ring flex w-full select-none items-center gap-4 rounded-card border-2 bg-surface p-3 text-left shadow-card transition-colors hover:bg-surface-subtle dark:shadow-none ${
                      isSelected ? "border-primary dark:border-accent" : "border-transparent"
                    }`}
                  >
                    <Avatar user={kid} size="lg" />
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-fg">
                        {kid.firstName} {kid.lastName}
                      </p>
                      <p className="text-sm text-fg-muted">
                        {new Date().getFullYear() - new Date(kid.dob).getFullYear()}{" "}
                        years
                      </p>
                    </div>
                    {isSelected && (
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-primary-fg">
                        <MdCheck aria-hidden="true" className="h-4 w-4" />
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>

          <div className="flex flex-col gap-3">
            <Button
              size="lg"
              fullWidth
              onClick={setupCheckIn}
              disabled={selected.length === 0}
              loading={checkingIn}
            >
              {selected.length === 0
                ? "Select at Least 1 Kid"
                : `Check In ${selected.length} Kid${selected.length > 1 ? "s" : ""}`}
            </Button>
            <Button
              variant="secondary"
              size="lg"
              fullWidth
              leftIcon={<MdAdd aria-hidden="true" className="h-5 w-5" />}
              onClick={() => setStep("add-child")}
            >
              Add Guest
            </Button>
          </div>
        </>
      )}

      {step === "add-child" && (
        <>
          <StepHeader
            description="Add a child or guest."
            onBack={() => navigate(`/member/check-in?phone=${phone}`)}
          />
          {errors.length > 0 && (
            <AlertPane tone="danger" className="">
              We couldn’t save these details. Check the highlighted fields and try again.
            </AlertPane>
          )}

          <NewChildForm onSubmit={onSubmit} errors={errors} />
        </>
      )}

      {step === "notice" && (
        <>
          <StepHeader
            description="Show this QR code to a staff member to check in your children."
            onBack={() => setStep("pick-kids")}
          />
          <CheckInCode parent={parent} checkinCode={checkinCode} />
        </>
      )}
    </PageContainer>
  );
};

export default CheckIn;
